import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { mainSiteUrl, seoKeywords } from '@/mocks/categories';
import { SITE_URL } from '@/constants';

const siteUrl = SITE_URL;
const articleUrl = `${siteUrl}/itaobuy-spreadsheet-sicurezza`;

const sicurezzaJsonLd = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Itaobuy Spreadsheet: Guida alla Sicurezza",
  "description": "La sicurezza dei tuoi dati è fondamentale. In questa guida analizziamo ogni aspetto della protezione offerta da Itaobuy Spreadsheet: crittografia, privacy, autenticazione e molto altro.",
  "image": "https://readdy.ai/api/search-image?query=Digital%20security%20concept%20with%20laptop%20displaying%20shield%20and%20lock%20icon%2C%20on%20warm%20oak%20wooden%20desk%2C%20soft%20natural%20window%20light%2C%20minimalist%20European%20workspace%2C%20modern%20professional%20atmosphere%2C%20warm%20neutral%20tones%2C%20no%20text%20no%20logos%2C%20clean%20composition&width=800&height=600&seq=sicurezza-hero&orientation=landscape",
  "datePublished": "2026-07-14",
  "dateModified": "2026-07-14",
  "author": {
    "@type": "Organization",
    "name": "Itaobuy Spreadsheet Italia"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Itaobuy Spreadsheet Italia",
    "logo": {
      "@type": "ImageObject",
      "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg"
    }
  },
  "url": articleUrl,
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": articleUrl
  },
  "inLanguage": "it"
};

export default function SicurezzaPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(sicurezzaJsonLd)
        }}
      />
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-background-100">
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className="flex items-center justify-between h-16 md:h-20">
            <a href="/" className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 tracking-tight hover:opacity-80 transition-opacity whitespace-nowrap">
              Itaobuy<span className="italic font-normal text-primary-500">Spreadsheet</span>
            </a>
            <div className="hidden md:flex items-center gap-5">
              <a href="/" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">Home</a>
              <a href="/itaobuy-spreadsheet-guida-acquisti" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">Guida Acquisti</a>
              <a href="/itaobuy-spreadsheet-recensione" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap">Recensioni</a>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="px-5 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">Buy It Now</a>
            </div>
          </div>
        </div>
      </nav>

      <section className="relative py-20 md:py-28 bg-background-50 overflow-hidden">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            <div className="flex-1">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
                <a href="/" className="hover:text-primary-500 transition-colors">Home</a>
                <span className="w-4 h-4 flex items-center justify-center">/</span>
                <span className="text-foreground-400">Sicurezza</span>
              </nav>
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold text-foreground-950 leading-tight mb-6">
                Itaobuy Spreadsheet:{' '}
                <span className="italic text-primary-500">Guida alla Sicurezza</span>
              </h1>
              <div className="flex items-center gap-3 text-sm text-foreground-500 mb-6">
                <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                <span>Aggiornato: Luglio 2026</span>
                <span className="text-foreground-300">|</span>
                <i className="ri-shield-check-line w-4 h-4 flex items-center justify-center text-emerald-500"></i>
                <span className="text-emerald-600 font-medium">Verificato</span>
              </div>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8 max-w-xl">
                La sicurezza dei tuoi dati è fondamentale. In questa guida analizziamo ogni
                aspetto della protezione offerta da Itaobuy Spreadsheet: crittografia, privacy,
                autenticazione e molto altro.
              </p>
              <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 text-white rounded-full text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                Torna alla Home
              </button>
            </div>
            <div className="flex-1">
              <div className="rounded-2xl overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=Digital%20security%20concept%20with%20laptop%20displaying%20shield%20and%20lock%20icon%2C%20on%20warm%20oak%20wooden%20desk%2C%20soft%20natural%20window%20light%2C%20minimalist%20European%20workspace%2C%20modern%20professional%20atmosphere%2C%20warm%20neutral%20tones%2C%20no%20text%20no%20logos%2C%20clean%20composition&width=800&height=600&seq=sicurezza-hero&orientation=landscape" className="w-full h-full object-cover" alt="Sicurezza Itaobuy Spreadsheet" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-4xl mx-auto">
          <article className="space-y-16">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-6">1. Crittografia e Protezione Dati</h2>
              <div className="space-y-4 text-foreground-700 leading-relaxed">
                <p><strong>Itaobuy Spreadsheet</strong> utilizza crittografia AES-256 bit, lo stesso standard adottato da banche e istituzioni governative in tutto il mondo. Ogni dato trasmesso tra il tuo dispositivo e i server di Itaobuy è protetto da protocollo TLS 1.3, garantendo che nessuno possa intercettare le tue informazioni durante il trasferimento.</p>
                <p>I dati archiviati sono crittografati sia a riposo che in transito, il che significa che anche in caso di accesso non autorizzato ai server fisici, le tue informazioni rimarrebbero illeggibili senza le chiavi di decrittazione. Per maggiori dettagli tecnici, consulta la nostra <a href="/itaobuy-spreadsheet-guida" className="text-primary-500 hover:text-primary-600 font-medium">guida completa</a>.</p>
              </div>
            </div>

            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-6">2. Come Proteggere il Tuo Account</h2>
              <div className="space-y-4 text-foreground-700 leading-relaxed">
                <p>La protezione del tuo account <strong>Itaobuy Sheet</strong> inizia da te. Ecco alcune buone pratiche fondamentali: utilizza sempre una password unica e complessa (almeno 12 caratteri con lettere, numeri e simboli), attiva l&apos;autenticazione a due fattori (2FA) che aggiunge un ulteriore livello di sicurezza oltre alla password, e non condividere mai le tue credenziali con nessuno.</p>
                <p>Itaobuy Spreadsheet offre anche notifiche di accesso in tempo reale: ogni volta che qualcuno accede al tuo account da un nuovo dispositivo, riceverai immediatamente un avviso via email. Questo ti permette di reagire rapidamente in caso di accessi non autorizzati. Leggi anche la nostra <a href="/itaobuy-spreadsheet-recensione" className="text-primary-500 hover:text-primary-600 font-medium">recensione completa</a> per scoprire cosa ne pensano gli utenti.</p>
              </div>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[{ icon: 'ri-lock-password-line', title: 'Password Sicura', desc: 'Usa almeno 12 caratteri con simboli, numeri e maiuscole. Cambiala regolarmente.' }, { icon: 'ri-smartphone-line', title: '2FA Attivo', desc: "Attiva l'autenticazione a due fattori per una protezione extra contro accessi non autorizzati." }, { icon: 'ri-notification-3-line', title: 'Notifiche Istantanee', desc: 'Ricevi avvisi immediati per ogni nuovo accesso al tuo account da dispositivi sconosciuti.' }].map((item, idx) => (
                  <div key={idx} className="p-5 rounded-xl bg-background-50 border border-background-200/70">
                    <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center text-rose-500 mb-3">
                      <i className={`${item.icon} w-5 h-5 flex items-center justify-center`}></i>
                    </div>
                    <h4 className="font-heading text-base font-semibold text-foreground-900 mb-1">{item.title}</h4>
                    <p className="text-sm text-foreground-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-6">3. Privacy e Conformità GDPR</h2>
              <div className="space-y-4 text-foreground-700 leading-relaxed">
                <p><strong>Itaobuy Spreadsheet</strong> è pienamente conforme al Regolamento Generale sulla Protezione dei Dati (GDPR) dell&apos;Unione Europea. Questo significa che i tuoi dati personali sono trattati secondo i più elevati standard di privacy, con piena trasparenza su come vengono raccolti, utilizzati e conservati.</p>
                <p>La piattaforma offre strumenti integrati per la gestione della privacy: puoi esportare tutti i tuoi dati in qualsiasi momento, richiedere la cancellazione completa del tuo account e gestire le preferenze di trattamento dei dati direttamente dal pannello di controllo. Ti consigliamo anche di leggere il nostro <a href="/itaobuy-spreadsheet-confronto" className="text-primary-500 hover:text-primary-600 font-medium">confronto con le alternative</a> per vedere come si posiziona in termini di privacy.</p>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-7xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950 mb-12">Articoli Correlati</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[{ title: 'Guida Completa 2026', url: '/itaobuy-spreadsheet-guida', icon: 'ri-book-open-line' }, { title: 'Recensione Dettagliata', url: '/itaobuy-spreadsheet-recensione', icon: 'ri-star-line' }, { title: 'Guida Acquisti Sicuri', url: '/itaobuy-spreadsheet-guida-acquisti', icon: 'ri-shopping-bag-3-line' }].map((item, i) => (
              <a key={i} href={item.url} className="p-6 rounded-xl bg-white border border-background-200/70 hover:shadow-md transition-all group cursor-pointer">
                <div className="w-12 h-12 rounded-xl bg-rose-50 flex items-center justify-center text-rose-500 mx-auto mb-4 group-hover:bg-rose-100 transition-colors">
                  <i className={`${item.icon} w-5 h-5 flex items-center justify-center`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-rose-500 transition-colors">{item.title}</h3>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-background-50">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-4xl mx-auto text-center">
          <p className="text-foreground-500 text-sm mb-4">
            Torna alla homepage per esplorare tutte le guide e le risorse su
            <a href="/" className="text-primary-500 hover:text-primary-600 font-medium ml-1">Itaobuy Spreadsheet Italia</a>
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-foreground-400">
            {seoKeywords.map((kw, idx) => (
              <span key={idx} className="px-3 py-1 rounded-full bg-white border border-background-200">{kw}</span>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-accent-900 text-white">
        <div className="w-full px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
          <div className="py-12 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <a href="/" className="font-heading text-2xl font-semibold text-white tracking-tight inline-block mb-4">Itaobuy<span className="italic font-normal text-accent-300">Spreadsheet</span></a>
              <p className="text-white/50 text-sm max-w-xs">La guida italiana più completa su Itaobuy Spreadsheet. Guide, recensioni, tutorial e confronti aggiornati.</p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <a href="/" className="text-white/60 text-sm hover:text-white transition-colors">Home</a>
                <a href="/itaobuy-spreadsheet-guida" className="text-white/60 text-sm hover:text-white transition-colors">Guida Completa</a>
                <a href="/itaobuy-spreadsheet-guida-acquisti" className="text-white/60 text-sm hover:text-white transition-colors">Guida Acquisti</a>
              </div>
              <div className="flex flex-col gap-2">
                <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-white/60 text-sm hover:text-white transition-colors">Buy It Now</a>
                <a href="#" className="text-white/60 text-sm hover:text-white transition-colors">Privacy Policy</a>
                <a href="#" className="text-white/60 text-sm hover:text-white transition-colors">Cookie Policy</a>
              </div>
            </div>
          </div>
          <div className="py-5 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-white/40 text-xs">&copy; {new Date().getFullYear()} Itaobuy Spreadsheet Italia.</p>
            <div className="flex items-center gap-3">
              {['ri-facebook-line', 'ri-twitter-x-line', 'ri-instagram-line'].map((icon) => (
                <a key={icon} href="#" className="w-7 h-7 rounded-md border border-white/20 flex items-center justify-center text-white/50 hover:text-white hover:border-white/40 transition-colors">
                  <i className={`${icon} w-3 h-3 flex items-center justify-center`}></i>
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
}